import { WebEnv } from '@easy-expense/env-mobile';
import React from 'react';

const STORAGE_KEY = 'debugDisablePaywall';

const listeners = new Set<() => void>();

/** The stored setting, or false when storage is unavailable. */
function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Never on outside staging, whatever was left in storage. */
function getDisablePaywall(): boolean {
  return WebEnv.isStaging && readStored();
}

/** Re-runs a listener on a change from this tab, or from another tab through `storage`. */
function subscribe(listener: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) {
      listener();
    }
  };
  listeners.add(listener);
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * Turns the staging Disable Paywall debug setting on or off for this browser. Kept in
 * localStorage so it survives reloads and QA account switches.
 */
function setDisablePaywall(value: boolean) {
  try {
    if (value) {
      localStorage.setItem(STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    return;
  }
  listeners.forEach((listener) => listener());
}

/**
 * Whether Disable Paywall is on: every plan-limited action goes through without a wall, while the
 * subscription itself is left untouched. Always false outside staging.
 */
function useDisablePaywall(): boolean {
  return React.useSyncExternalStore(subscribe, getDisablePaywall);
}

export { setDisablePaywall, useDisablePaywall };
